import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import api from '../../api/axios';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const Settings = () => {
    const [branding, setBranding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [removingImage, setRemovingImage] = useState(false);

    // Aktiv sub-menu modal: null | 'service' | 'screen'
    const [activeModal, setActiveModal] = useState(null);

    // Servis faizi tənzimləmələri
    const [serviceMinAmount, setServiceMinAmount] = useState('');
    const [servicePercent, setServicePercent] = useState(0);
    const [savingServiceMin, setSavingServiceMin] = useState(false);

    const loadServiceSettings = async () => {
        try {
            const { data } = await api.get('/auth/tenant-service-percent');
            setServiceMinAmount(String(data?.service_min_amount ?? ''));
            setServicePercent(Number(data?.service_percent || 0));
        } catch { /* ignore */ }
    };

    const saveServiceMin = async () => {
        setSavingServiceMin(true);
        try {
            await api.put('/auth/service-min-amount', { amount: Number(serviceMinAmount || 0) });
            toast.success('Minimum servis məbləği yeniləndi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setSavingServiceMin(false);
        }
    };

    useEffect(() => {
        loadBranding();
        loadServiceSettings();
    }, []);

    const loadBranding = async () => {
        try {
            const res = await api.get('/auth/login-screen-settings');
            setBranding(res.data.data);
        } catch (err) {
            toast.error('Tənzimləmələr yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';

        if (!file) return;

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error('Şəkil ölçüsü maksimum 5 MB ola bilər.');
            return;
        }

        const formData = new FormData();
        formData.append('image', file, file.name);

        setUploadingImage(true);
        try {
            const res = await api.post('/auth/login-screen-image', formData);

            setBranding((prev) => ({
                ...(prev || {}),
                login_image_pending_url: res.data.image_url,
                login_image_pending: true
            }));

            toast.success(res.data?.msg || 'Şəkil təsdiq üçün göndərildi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Şəkil yüklənmədi.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handlePendingRemove = async () => {
        setRemovingImage(true);
        try {
            await api.delete('/auth/login-screen-image');
            setBranding((prev) => ({
                ...(prev || {}),
                login_image_pending_url: null,
                login_image_pending: false
            }));
            toast.success('Gözləyən şəkil silindi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Şəkil silinmədi.');
        } finally {
            setRemovingImage(false);
        }
    };

    const previewImage = branding?.login_image_pending_url || branding?.login_image_url || null;
    const previewStyle = previewImage
        ? {
              backgroundImage: `linear-gradient(rgba(5, 17, 27, 0.16), rgba(5, 17, 27, 0.34)), url(${previewImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
          }
        : {
              backgroundImage: 'linear-gradient(rgba(5, 17, 27, 0.16), rgba(5, 17, 27, 0.34)), url(/default_background.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
          };

    const MENU = [
        { key: 'printers', icon: '🖨️', title: 'Printerlər',     desc: 'Çap cihazları, A4 / termal', color: 'blue',    href: '/admin/printers' },
        { key: 'service',  icon: '💰', title: 'Servis faizi',    desc: 'Faiz və minimum məbləğ',     color: 'amber',   modal: 'service' },
        { key: 'screen',   icon: '🖼️', title: 'Giriş ekranı',    desc: 'Login ekran şəkli',          color: 'violet',  modal: 'screen' },
    ];
    const COLORS = {
        blue:    'border-blue-100 hover:border-blue-300 bg-blue-50/40',
        amber:   'border-amber-100 hover:border-amber-300 bg-amber-50/40',
        violet:  'border-violet-100 hover:border-violet-300 bg-violet-50/40',
        emerald: 'border-emerald-100 hover:border-emerald-300 bg-emerald-50/40',
    };

    return (
        <Layout>
            <div className="max-w-5xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-black text-gray-800">⚙️ Tənzimləmələr</h1>
                    <p className="mt-1 text-sm text-gray-500">Bölmə seçin</p>
                </div>

                {/* Sub-menu kartları */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {MENU.map(m => {
                        const inner = (
                            <>
                                <div className="text-3xl mb-2">{m.icon}</div>
                                <div className="font-bold text-gray-800 text-sm">{m.title}</div>
                                <div className="text-[11px] text-gray-400 mt-0.5">{m.desc}</div>
                            </>
                        );
                        const cls = `flex flex-col items-start rounded-2xl border-2 p-5 transition-all hover:shadow-md cursor-pointer ${COLORS[m.color]}`;
                        return m.href ? (
                            <a key={m.key} href={m.href} className={cls}>{inner}</a>
                        ) : (
                            <button key={m.key} type="button" onClick={() => setActiveModal(m.modal)} className={`${cls} text-left`}>{inner}</button>
                        );
                    })}
                </div>
            </div>

            {/* ── Servis faizi modal ── */}
            {activeModal === 'service' && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                    onClick={(e) => e.target === e.currentTarget && setActiveModal(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h2 className="text-base font-bold text-amber-900 flex items-center gap-2">💰 Servis faizi tənzimləmələri</h2>
                            <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-5">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-amber-700">Aktiv servis faizi</label>
                                    <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 font-mono font-bold text-amber-800">
                                        {servicePercent}% <span className="text-[10px] font-normal text-amber-600">(superadmin)</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-amber-700">Minimum sifariş məbləği (₼)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={serviceMinAmount}
                                        onChange={(e) => setServiceMinAmount(e.target.value)}
                                        placeholder="0 = həmişə tətbiq olunur"
                                        className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-mono text-sm focus:outline-none focus:border-amber-400"
                                    />
                                </div>
                            </div>
                            <p className="text-[11px] text-amber-700/80 mt-3 leading-relaxed">
                                💡 Sifarişin cəmi bu məbləğə bərabər və ya aşağı olarsa servis haqqı tətbiq olunmayacaq.
                                Misal: 10 ₼ yazsanız, 10 ₼ və aşağı sifarişdə servis 0%, 10.01 ₼-dən yuxarı {servicePercent}% olacaq.
                            </p>
                            <div className="flex gap-2 mt-5">
                                <button onClick={() => setActiveModal(null)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">Bağla</button>
                                <button onClick={saveServiceMin} disabled={savingServiceMin}
                                    className="flex-1 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold disabled:opacity-50">
                                    {savingServiceMin ? '...' : '💾 Saxla'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Giriş ekranı modal ── */}
            {activeModal === 'screen' && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto"
                    onClick={(e) => e.target === e.currentTarget && setActiveModal(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-4">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">🖼️ Giriş ekranını dəyiş</h2>
                            <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-5">
                            {loading ? (
                                <div className="flex h-48 items-center justify-center text-gray-400">Yüklənir...</div>
                            ) : (
                                <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-500">
                                            Yüklədiyiniz şəkil əvvəlcə superadmin təsdiqinə gedəcək. Təsdiqdən sonra waiter giriş ekranında görünəcək.
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-3">
                                            <label className="inline-flex cursor-pointer items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 font-semibold text-white hover:bg-primary-700">
                                                {uploadingImage ? 'Yüklənir...' : 'Şəkil yüklə'}
                                                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploadingImage} />
                                            </label>
                                            <button type="button" onClick={handlePendingRemove}
                                                disabled={removingImage || !branding?.login_image_pending_url}
                                                className="rounded-xl border border-red-200 px-4 py-2.5 font-semibold text-red-500 hover:bg-red-50 disabled:opacity-50">
                                                {removingImage ? 'Silinir...' : 'Gözləyən şəkli sil'}
                                            </button>
                                        </div>
                                        <div className="mt-4 space-y-1 text-sm text-gray-500">
                                            <p>Maksimum: 5 MB · JPG, PNG, WEBP</p>
                                        </div>
                                        <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
                                            <div className="font-semibold text-gray-700">Hazırkı status</div>
                                            <div className="mt-2 text-gray-600">
                                                {branding?.login_image_pending_url
                                                    ? 'Yeni şəkil superadmin təsdiqini gözləyir.'
                                                    : branding?.login_image_url
                                                        ? 'Aktiv təsdiqlənmiş şəkil istifadə olunur.'
                                                        : 'Hazırda xüsusi giriş şəkli yoxdur.'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-full lg:w-[340px]">
                                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Preview</div>
                                        <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-gray-200" style={previewStyle}>
                                            <div className="relative z-10 flex h-full flex-col justify-between p-5 text-center">
                                                <div className="pt-6">
                                                    <img src="/fiscallogo_blue.png" alt="Fiscal logo" className="mx-auto mb-4 h-12 w-auto" />
                                                    <h4 className="mb-2 text-2xl font-black text-white" style={{ fontFamily: 'serif' }}>
                                                        {branding?.app_name || 'Fiscal POS'}
                                                    </h4>
                                                    <p className="text-[11px] uppercase tracking-[0.3em] text-white/85">Restoran idarəetmə sistemi</p>
                                                </div>
                                                <div>
                                                    <div className="font-mono text-2xl text-white">12:45:09</div>
                                                    <div className="mt-1 text-xs text-white/80">Çərşənbə, 7 May 2026</div>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold text-white underline underline-offset-4">Powered by www.fiscalbusiness.net</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </Layout>
    );
};

export default Settings;
